import { z } from 'zod';
import { DecorKindSchema, LabColorSchema } from './decors';

/**
 * Контракт `/api/decors/match` — подбор декора по фото пользователя
 * (PRD §5 Экран 2.2.3 + ADR-0002 + ADR-0006 + ADR-0009).
 *
 * Критичные инварианты (P-009 / ADR-0009):
 *   - На бэк уходят ТОЛЬКО числа/literal'ы + опц. каталожные фильтры-строки.
 *   - Никаких Blob / File / base64 / URL фото в запросе.
 *   - Защита: `.strict()` в Zod-схеме, type-уровень в `extractLab(ImageData, Region)`,
 *     ESLint allowlist для `extractLab`, E2E network snapshot
 *     (см. `apps/web/e2e/decors-p009-network-check.spec.ts` — Phase 9).
 *
 * PR-FLOW (ADR-0043): подбор по фото ищет ВНУТРИ выбранного фильтра. Добавлены
 * опц. `subcategories?/brands?/types?` (массивы строк). `.strict()` СОХРАНЁН —
 * это публичные каталожные фильтры (словарь), НЕ ПДн и НЕ фото. Строгая per-kind
 * валидация subcategory — в хендлере через switch(kind) + раздельные
 * filterFacades/filterCountertops/filterPanels (P-033), не в контракте.
 */

// ── Mode (P-037) ──────────────────────────────────────────────────────────
// 'area' = LAB по выделенному кругу-пикеру (Экран 2.2.3 точный сценарий).
// 'average' = LAB по среднему всего фото (быстрый сценарий).
export const DecorMatchModeSchema = z.enum(['area', 'average']);
export type DecorMatchMode = z.infer<typeof DecorMatchModeSchema>;

// ── Region (для lab/extract.ts — image-pixel space, ADR-0008) ─────────────
// Координаты круга в исходном `ImageData` (не в screen-px). Используется
// в pure-функции `extractLab(imageData, region)` (Phase 2). На бэк НЕ уходит —
// клиент сам считает LAB и шлёт {l, a, b}.
export const RegionSchema = z.discriminatedUnion('type', [
  z.object({
    type: z.literal('circle'),
    cx: z.number().nonnegative(),
    cy: z.number().nonnegative(),
    r: z.number().positive(),
  }),
  z.object({ type: z.literal('average') }),
]);
export type Region = z.infer<typeof RegionSchema>;

// ── Request (.strict() — ключевая защита P-009) ──────────────────────────
// `.strict()` блокирует любые posторонние поля при runtime-валидации
// (даже если type-checker обойдён — сервер вернёт INVALID_INPUT).
//
// Любая попытка добавить новое поле (`photoBlob`, `mask`, `thumbnail`) →
// ОБЯЗАТЕЛЬНО обновить ADR-0009 «Откат» и полное reasoning, прежде чем
// снимать `.strict()` или расширять схему.
export const DecorMatchRequestSchema = z
  .object({
    kind: DecorKindSchema,
    mode: DecorMatchModeSchema,
    l: LabColorSchema.shape.l,
    a: LabColorSchema.shape.a,
    b: LabColorSchema.shape.b,
    // PR-FLOW (ADR-0043): опц. фильтр-scoping подбора — сужает каталог ДО
    // LAB-ранжирования. Массивы строк (не per-kind enum): per-kind строгая
    // валидация subcategory выполняется в хендлере (switch(kind) + раздельные
    // filterFacades/filterCountertops/filterPanels, P-033). brands/types во
    // всех трёх *FilterSchema и так z.array(z.string()). НЕ ПДн, НЕ фото —
    // .strict() остаётся и по-прежнему блокирует photo/blob/base64/url/mask.
    subcategories: z.array(z.string()).optional(),
    brands: z.array(z.string()).optional(),
    types: z.array(z.string()).optional(),
  })
  .strict();
export type DecorMatchRequest = z.infer<typeof DecorMatchRequestSchema>;

// ── Response ──────────────────────────────────────────────────────────────
// PRD §5 Экран 2.2.6: «5 карточек, фото, название, бренд, дельта (Δ)».
export const DecorMatchResponseItemSchema = z.object({
  globalId: z.string().min(1), // P-001 verbatim
  name: z.string().min(1),
  brand: z.string().min(1),
  deltaE: z.number().nonnegative(), // расстояние LAB (ΔE76 для MVP, ADR-0006)
  primaryPhotoKey: z.string().nullable(),
});
export type DecorMatchResponseItem = z.infer<typeof DecorMatchResponseItemSchema>;

// `metric` нужен для аналитики и будущей миграции на ΔE2000 (B-014).
// Когда будем менять метрику — изменится только это поле.
export const DecorMatchResponseSchema = z.object({
  items: z.array(DecorMatchResponseItemSchema),
  metric: z.enum(['dE76', 'dE2000']),
});
export type DecorMatchResponse = z.infer<typeof DecorMatchResponseSchema>;
